import { useEffect, useMemo, useState } from 'react';

function RequiredBadge() {
  return (
    <span className="ml-2 inline-flex items-center rounded-sm border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] leading-none text-red-500 align-middle">
      必須
    </span>
  );
}

export default function ResponseModal({ open, onClose, onSubmit, defaultValues }) {
  const [attendance, setAttendance] = useState(''); // 'present' | 'absent'
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [allergy, setAllergy] = useState('');
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);

  const MAX_NAME_LEN = 20;
  const MAX_TEXT_LEN = 200;
  const codePointLength = (text) => Array.from(text || '').length;

  const isValid = useMemo(() => {
    const hasRequired = attendance !== '' && lastName.trim() !== '' && firstName.trim() !== '';
    const withinLimits =
      codePointLength(lastName) <= MAX_NAME_LEN &&
      codePointLength(firstName) <= MAX_NAME_LEN &&
      codePointLength(allergy) <= MAX_TEXT_LEN &&
      codePointLength(comment) <= MAX_TEXT_LEN;
    return hasRequired && withinLimits;
  }, [attendance, lastName, firstName, allergy, comment]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Apply default values when opening or when defaults change
  useEffect(() => {
    if (!open) return;
    if (defaultValues) {
      if (typeof defaultValues.attendance === 'string') setAttendance(defaultValues.attendance || '');
      if (typeof defaultValues.lastName === 'string') setLastName(defaultValues.lastName || '');
      if (typeof defaultValues.firstName === 'string') setFirstName(defaultValues.firstName || '');
      if (typeof defaultValues.allergy === 'string') setAllergy(defaultValues.allergy || '');
      if (typeof defaultValues.comment === 'string') setComment(defaultValues.comment || '');
      setTouched(false);
    }
  }, [open, defaultValues]);

  if (!open) return null;

  const submit = () => {
    setTouched(true);
    if (!isValid) return;
    const payload = { attendance, lastName: lastName.trim(), firstName: firstName.trim(), allergy, comment };
    if (onSubmit) onSubmit(payload);
  };

  const FieldLabel = ({ children }) => (
    <div className="mb-2 text-[13px] font-serifjp text-gray-700 text-center">
      {children}
    </div>
  );

  const choiceBase = (selected) =>
    `w-full rounded-full py-3 px-4 flex items-center justify-center space-x-2 border ${
      selected ? 'border-[#efb1a7] bg-[#fff7f6] shadow-sm' : 'border-gray-200 bg-white'
    }`;

  const radioOuter = (selected) =>
    `inline-flex items-center justify-center w-4 h-4 rounded-full border ${
      selected ? 'border-[#e07a6a]' : 'border-gray-300'
    }`;

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-5 flex justify-center items-end">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg overflow-hidden" role="dialog" aria-modal="true">
          <div className="max-h-[80vh] flex flex-col">
            <div className="px-5 pt-5 pb-3 text-center">
              <h2 className="font-heading text-[#8f5c58] text-[18px]">ご出欠のご回答</h2>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-4">
              {/* 出欠 */}
              <FieldLabel>
                ご出欠
                <RequiredBadge />
              </FieldLabel>
              <div className="grid grid-cols-2 gap-4">
                <button type="button" className={choiceBase(attendance === 'present')} onClick={() => setAttendance('present')}>
                  <span className={radioOuter(attendance === 'present')}>
                    <span className={`w-2 h-2 rounded-full ${attendance === 'present' ? 'bg-[#e07a6a]' : 'bg-gray-200'}`} />
                  </span>
                  <span className="text-[13px] font-serifjp text-gray-700">ご出席</span>
                </button>
                <button type="button" className={choiceBase(attendance === 'absent')} onClick={() => setAttendance('absent')}>
                  <span className={radioOuter(attendance === 'absent')}>
                    <span className={`w-2 h-2 rounded-full ${attendance === 'absent' ? 'bg-[#e07a6a]' : 'bg-gray-200'}`} />
                  </span>
                  <span className="text-[13px] font-serifjp text-gray-700">ご欠席</span>
                </button>
              </div>
              {touched && attendance === '' && (
                <p className="mt-2 text-xs text-red-500 text-center">ご出欠を選択してください</p>
              )}

              {/* お名前 */}
              <div className="mt-6">
                <FieldLabel>
                  お名前
                  <RequiredBadge />
                </FieldLabel>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="姓"
                      className={`w-full rounded border px-3 py-2 text-[16px] sm:text-[13px] ${
                        touched && (lastName.trim() === '' || codePointLength(lastName) > MAX_NAME_LEN)
                          ? 'border-red-300'
                          : 'border-gray-200'
                      }`}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="名"
                      className={`w-full rounded border px-3 py-2 text-[16px] sm:text-[13px] ${
                        touched && (firstName.trim() === '' || codePointLength(firstName) > MAX_NAME_LEN)
                          ? 'border-red-300'
                          : 'border-gray-200'
                      }`}
                    />
                  </div>
                </div>
                {touched && (lastName.trim() === '' || firstName.trim() === '') && (
                  <p className="mt-2 text-xs text-red-500 text-center">姓と名を入力してください</p>
                )}
                {(codePointLength(lastName) > MAX_NAME_LEN || codePointLength(firstName) > MAX_NAME_LEN) && (
                  <p className="mt-2 text-xs text-red-500 text-center">姓・名は各20文字以内で入力してください</p>
                )}
              </div>

              {/* アレルギー */}
              <div className="mt-6">
                <div className="mb-2 text-center">
                  <p className="text-[15px] font-heading text-[#8f5c58]">アレルギーについて</p>
                  <p className="mt-2 text-[12px] text-gray-500 font-serifjp">アレルギーなど食べられないものがございましたらご遠慮なくお書き添えください</p>
                </div>
                <textarea
                  value={allergy}
                  onChange={(e) => setAllergy(e.target.value)}
                  placeholder="例）えび・かに・乳製品 など"
                  className={`w-full h-28 rounded border px-3 py-2 text-[16px] sm:text-[13px] ${
                    codePointLength(allergy) > MAX_TEXT_LEN ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                {codePointLength(allergy) > MAX_TEXT_LEN && (
                  <p className="mt-2 text-xs text-red-500 text-center">200文字以内で入力してください</p>
                )}
              </div>

              {/* コメント */}
              <div className="mt-6">
                <div className="mb-2 text-center">
                  <p className="text-[15px] font-heading text-[#8f5c58]">コメント</p>
                </div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="メッセージがあればご記入ください！！"
                  className={`w-full h-28 rounded border px-3 py-2 text-[16px] sm:text-[13px] ${
                    codePointLength(comment) > MAX_TEXT_LEN ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                {codePointLength(comment) > MAX_TEXT_LEN && (
                  <p className="mt-2 text-xs text-red-500 text-center">200文字以内で入力してください</p>
                )}
              </div>
            </div>

            {/* アクションバー */}
            <div className="sticky bottom-0 flex">
              <button
                type="button"
                onClick={onClose}
                className="w-1/2 py-4 bg-gray-50 text-gray-900 text-[15px] font-sans"
              >
                閉じる
              </button>
              <button
                type="button"
                onClick={submit}
                className="w-1/2 py-4 bg-emerald-600 text-white text-[15px] font-sans"
                disabled={!isValid && touched}
              >
                送信
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


